/**
 * Row F of the Channel Deep-dive (spec §3.2, §4): D1's filter bar reused whole
 * (period, custom range, objective, currency) without its channel dropdown, the
 * page being one channel (R20), then Campaign type and Campaign. Their options
 * come from the entities: loading → both disabled; failed → both say so and
 * neither applies (R8); options null = the GA4 tab, where neither means anything
 * (spec §3.5), so neither is drawn. A null type label is "ไม่ระบุประเภท" (typeLabel).
 * In all workspaces a campaign's label carries its workspace's name (R25), so two
 * same-named campaigns stay apart and a workspace is searchable.
 *
 * @module components/channels/ChannelFilterBar
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewFilterBar } from '@/components/overview/OverviewFilterBar';
import { typeLabel } from '@/services/channels/channelFormat';
import type { CampaignOption, FilterOptions } from '@/services/channels/channelTypes';
import type { BoxState, CurrencyChoice, ObjectiveFilter } from '@/services/overview/overviewTypes';
import { MultiSelectFilter, type MultiSelectOption } from './MultiSelectFilter';

interface Props {
  token: string;
  onToken: (t: string) => void;
  objective: ObjectiveFilter;
  onObjective: (o: ObjectiveFilter) => void;
  money: CurrencyChoice | null;
  onCurrency: (c: string) => void;
  /** null = the GA4 tab: no type or campaign control. */
  options: BoxState<FilterOptions> | null;
  types: readonly string[];
  onTypes: (v: string[]) => void;
  campaigns: readonly string[];
  onCampaigns: (v: string[]) => void;
  disabled?: boolean;
}

type PickerProps = Pick<Props, 'types' | 'onTypes' | 'campaigns' | 'onCampaigns'> & {
  options: BoxState<FilterOptions>;
  disabled: boolean;
};

const campaignLabel = (c: CampaignOption): string => (c.workspaceName ? `${c.name} · ${c.workspaceName}` : c.name);

const Pickers: React.FC<PickerProps> = ({ options, types, onTypes, campaigns, onCampaigns, disabled }) => {
  const { t } = useTranslation();
  const ready = options.status === 'ready' ? options.data : null;
  const failed = options.status === 'failed';
  const off = disabled || ready === null;
  const typeOptions: MultiSelectOption[] = (ready?.types ?? []).map((o) => ({ value: o.value, label: typeLabel(o.label, t) }));
  const campaignOptions: MultiSelectOption[] = (ready?.campaigns ?? []).map((c) => ({ value: c.value, label: campaignLabel(c) }));
  return (
    <>
      <MultiSelectFilter label={t('channels.filters.type')} options={typeOptions} selected={types} onChange={onTypes}
        disabled={off} failed={failed} />
      <MultiSelectFilter label={t('channels.filters.campaign')} options={campaignOptions} selected={campaigns} onChange={onCampaigns}
        disabled={off} failed={failed} />
    </>
  );
};

export const ChannelFilterBar: React.FC<Props> = ({
  token, onToken, objective, onObjective, money, onCurrency, options, types, onTypes, campaigns, onCampaigns, disabled = false,
}) => (
  <OverviewFilterBar
    token={token} onToken={onToken} objective={objective} onObjective={onObjective}
    money={money} onCurrency={onCurrency} disabled={disabled} channelFilter={false}
  >
    {options && (
      <Pickers options={options} types={types} onTypes={onTypes} campaigns={campaigns} onCampaigns={onCampaigns} disabled={disabled} />
    )}
  </OverviewFilterBar>
);
